import React from 'react';
import { SettingsFormikContainer } from '../../../components/common/SettingsFormikContainer';
import { ISiteAdminBridgeSettings, ISiteBridgeSettingsForSiteAdmin } from '~src/settings/types/ISiteSettingsForSiteAdmin';
import { Sites } from '~src/ts/siteManagement/services/resources/Sites';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { IRemoteAgentConnectionStatus } from '~src/ts/ServerApi/requests/GetSiteRemoteAgentsConnectionStatusRequest';
import { incrementRouterSpaceReactKey } from '~src/app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import { getAppStore } from '~src/app/AppStore';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { ToasterService, ToastType } from '~src/ts/services/ToasterService';
import { Messages } from '~src/codegen/Localize';
import { ServerUsers } from '~src/userManagement/services/resources/ServerUsers';

// the only purpose of this type's existence is to map to
// the props(which is the same as the response body for the getbridge settings API call)
// that magically get sent to this component
export type SiteBridgeSettingsForSiteAdminProps = {
  settings: { // this is the same as the type of the response body  in the get bridge settings API response
    id?: string; // id shouldnt be in the settings passed on to settings
    siteAdminBridgeSettings?: ISiteAdminBridgeSettings;
  };
  connectionStatuses?: IRemoteAgentConnectionStatus[];
};

const bridgeSiteSettingsSections = [
  'BridgeSettingsSummarySection',
  'BridgeClientNotRunningNotificationsSection',
  'BridgePoolingSection',
  'BridgeRemoteAgentsSection'
];


export function BridgeSiteSettings(props: SiteBridgeSettingsForSiteAdminProps) {
  const submitHandler = useSubmitHandler(props);

  return <SettingsFormikContainer
    formName={'BridgeSiteSettings'} // this is required
    onSubmit={submitHandler} // submitHandler gets called when the save button is clicked.
    sections={bridgeSiteSettingsSections}
    {...props}
  />;
}

function useSubmitHandler(settings: SiteBridgeSettingsForSiteAdminProps) {
  return React.useCallback(async (newSiteAdminBridgeSettings: SiteBridgeSettingsForSiteAdminProps['settings']) => {

    /**
     * Permanently remove deleted agents on save. If remove fails, add agent back to formik settings
     */
    const oldAgents = settings.settings.siteAdminBridgeSettings.remoteAgentSettings.agents;
    const newAgents = newSiteAdminBridgeSettings.siteAdminBridgeSettings.remoteAgentSettings.agents;
    if (oldAgents.length > newAgents.length) {
      const newAgentsSet = new Set(newAgents);
      const deletedAgents = oldAgents.filter(agent => !newAgentsSet.has(agent));

      deletedAgents.forEach((agent) => {
        ServerUsers.instance.deleteUserRemoteAgents(agent.ownerId, [agent.deviceId])
          .then((result: IResult) => {
            if (!result || (result.errors && result.errors.length)) {
              ToasterService.getInstance().pop({ type: ToastType.Error, text: Messages.errorUnknown() });
              newSiteAdminBridgeSettings.siteAdminBridgeSettings.remoteAgentSettings.agents.push(agent);
            }
          });
      });
    }

    const siteId = newSiteAdminBridgeSettings.id;

    const sitesService = Sites.instance;
    const result: ISiteAdminBridgeSettings | IResult = await sitesService.updateSiteBridgeSettingsForSiteAdmin(
      siteId, newSiteAdminBridgeSettings.siteAdminBridgeSettings as ISiteAdminBridgeSettings);

    if (result?.errors?.length) {
      throw result.errors.map((e: any) => sitesService.errorMessage(e.code, null));
    } else {
      /**
        * Have to use a hard reload here to force settings to update with newly saved values
        * TODO: This solution could be improved in the future
        */
      await getSessionManagementServiceInstance().refreshSessionInfo();
      getAppStore().dispatch(incrementRouterSpaceReactKey());

      ToasterService.getInstance().pop({ type: ToastType.Success, text: Messages.settingsUpdateSuccess() });

      // Return a snapshot of saved settings in case API call fails without error
      const savedSettings: ISiteBridgeSettingsForSiteAdmin = await sitesService.getSiteBridgeSettingsForSiteAdminById(siteId);
      return {
        settings: savedSettings,
        connectionStatuses: settings.connectionStatuses
      };
    }
  }, [settings]);
}